// Main Application Controller - Updated for modular navigation
class App {
    constructor() {
        // console.log('App constructor called');
        this.isInitialized = false;
        this.environment = window.Environment || null;
        
        this.initializeApp();
    }

    /**
     * Show loading overlay with message
     * @param {boolean} show - Whether to show or hide loading
     * @param {string} message - Loading message
     */
    showLoading(show, message = 'Loading...') {
        if (window.Helpers && window.Helpers.showLoading) {
            window.Helpers.showLoading(show, message);
        }
    }

    /**
     * Show error message
     * @param {string} message - Error message
     * @param {number} duration - Display duration in ms
     * @param {string} type - Error type
     */
    showError(message, duration = 5000, type = 'error') {
        if (window.Helpers && window.Helpers.showError) {
            window.Helpers.showError(message, duration, type);
        }
    }

    /**
     * Initialize the application
     */
    async initializeApp() {
        try {
            // SECURITY CHECK - Ensure HTTPS (except localhost dev)
            const isLocalDev = window.location.hostname === 'localhost' || 
                              window.location.hostname === '127.0.0.1';
            const isSecureContext = window.isSecureContext || false;
            
            if (!isLocalDev && !isSecureContext) {
                console.error('❌ INSECURE CONTEXT - HTTPS required');
                this.showError(
                    '⚠️ Security Error: This app requires HTTPS. Web Crypto API is unavailable.',
                    0, // No auto-dismiss
                    'security'
                );
            }
            
            // Configure remote Hyperliquid node - FORCE UPDATE to node2.evplus.ai
            const currentNodeUrl = localStorage.getItem('remoteNodeUrl');
            const targetNodeUrl = 'https://node.evplus.ai';
            
            if (currentNodeUrl !== targetNodeUrl) {
                console.log(`🔄 Updating node URL: ${currentNodeUrl} → ${targetNodeUrl}`);
                localStorage.setItem('remoteNodeUrl', targetNodeUrl);
                localStorage.setItem('useRemoteNode', 'true');
            }
            
            // console.log('🌐 Using remote node:', localStorage.getItem('remoteNodeUrl'));
            
            // console.log('🔐 Starting app initialization - checking authentication...');
            // AUTHENTICATION GATE - Check password before app initialization
            const isAuthenticated = await this.authenticateUser();
            // console.log('🔐 Authentication result:', isAuthenticated);
            
            if (isAuthenticated) {
                // Check if this is first-time setup (no stored API key)
                const hasStoredKey = window.PasswordManager.hasStoredApiKey();
                
                if (!hasStoredKey) {
                    // console.log('🆕 [APP-INIT] First-time user detected - showing onboarding notification');
                    
                    // Show onboarding notification after short delay
                    setTimeout(() => {
                        if (window.NotificationManager) {
                            window.NotificationManager.showNotification({
                                type: 'info',
                                title: 'Welcome to ATV4! 👋',
                                message: `Get started by adding your Hyperliquid API key and approving builder fees. <a href="https://atsetup.evplus.ai" target="_blank" style="color: #00BF63; text-decoration: underline; font-weight: 600;">Setup builder fee here →</a>`,
                                duration: 0 // No auto-dismiss - user must close manually
                            });
                        }
                    }, 2000);
                }
                
            } else {
                // console.log('❌ Authentication failed or cancelled');
                return;
            }
            
            // Ensure global loading overlay stays hidden
            const globalOverlay = document.getElementById('loading-overlay');
            if (globalOverlay) {
                globalOverlay.classList.add('hidden');
                globalOverlay.style.display = 'none';
            }
            
            // Check if Helpers is available
            if (!window.Helpers) {
                await new Promise(resolve => setTimeout(resolve, 50));
            }
            
            // Parallelize independent initialization steps for faster load
            await Promise.all([
                this.initializeComponents(),
                this.initializeNavigation(),
                this.initializeOrderSystem(),
                this.initializeModeManager()
            ]);
            
            // Show mode switcher after successful authentication
            const modeSwitcher = document.getElementById('mode-switcher-overlay');
            if (modeSwitcher) {
                modeSwitcher.classList.add('authenticated');
                // console.log('✅ Mode switcher enabled');
            }
            
            // Initialize global event listeners (non-blocking)
            this.setupEventListeners();
            
            // Start SSE connection in background (non-blocking)
            this.connectToSSE().catch(err => console.warn('SSE connection failed:', err));
            
            this.setEnvironmentInfo();
            
            this.isInitialized = true;
            
            // CRITICAL: Clear any stale cached position data from previous sessions
            // This ensures Dashboard starts with fresh data only
            if (window.lastPositionsData) {
                // console.log('🔄 Clearing stale position cache from previous session');
                delete window.lastPositionsData;
            }
            
            // Load default view AFTER all initialization is complete
            if (window.NavigationManager && window.NavigationManager.loadDefaultView) {
                window.NavigationManager.loadDefaultView();
            }
            
        } catch (error) {
            this.showError('Failed to initialize application: ' + error.message);
            console.error('App initialization error:', error);
            return;  // Stop initialization on error
        }
    }

    /**
     * Authenticate user with password modal
     * @returns {Promise<boolean>} - Authentication success
     */
    async authenticateUser() {
        try {
            // console.log('🔑 authenticateUser() called');
            
            // Fast-check: Wait for PasswordManager with much shorter timeout
            if (!window.PasswordManager || !window.PasswordManager.isReady) {
                // Wait max 1 second instead of 5 seconds
                await new Promise(resolve => {
                    let attempts = 0;
                    const check = () => {
                        if (window.PasswordManager?.isReady || attempts >= 20) {
                            resolve();
                        } else {
                            attempts++;
                            setTimeout(check, 50); // 50ms × 20 = 1s max
                        }
                    };
                    check();
                });
            }
            
            if (!window.PasswordManager || !window.PasswordManager.isReady) {
                throw new Error('PasswordManager not initialized');
            }
            
            // NEW: Check if another tab is already authenticated
            const gotAuthFromOtherTab = await window.PasswordManager.tryGetAuthFromOtherTab();
            if (gotAuthFromOtherTab) {
                // Successfully authenticated from another tab - skip password modal
                await Promise.all([
                    // Load Hyperliquid credentials (critical path)
                    window.PasswordManager.loadApiCredentials()
                        .then(() => {
                            // console.log('✅ Hyperliquid credentials loaded');
                        })
                        .catch(error => {
                            console.warn('⚠️ Hyperliquid credentials not found');
                        }),
                    
                    // Load Lighter credentials in parallel (non-blocking)
                    (async () => {
                        if (window.LighterCredentialsManager) {
                            try {
                                const lighterCreds = await window.LighterCredentialsManager.loadLighterCredentials();
                                // console.log('✅ Lighter credentials loaded');
                                
                                // Initialize Lighter client if credentials exist
                                if (lighterCreds && lighterCreds.apiKey && window.LighterClient) {
                                    await window.LighterClient.initialize(
                                        lighterCreds.apiKey,
                                        lighterCreds.publicAddress,
                                        lighterCreds.accountIndex,
                                        lighterCreds.apiKeyIndex,
                                        lighterCreds.network
                                    );
                                    // console.log('✅ Lighter client initialized');
                                }
                            } catch (error) {
                                console.warn('⚠️ Lighter credentials error:', error.message);
                            }
                        }
                    })()
                ]);
                
                return true;
            }
            
            // No authenticated tabs found - show password modal
            // Fast-check: PasswordModal should be ready quickly
            if (!window.PasswordModal) {
                await new Promise(resolve => setTimeout(resolve, 50));
                
                if (!window.PasswordModal) {
                    console.error('❌ PasswordModal component not loaded');
                    throw new Error('PasswordModal component not loaded');
                }
            }

            // console.log('✅ Creating PasswordModal instance...');
            // Show password modal and wait for authentication
            const passwordModal = new window.PasswordModal();
            // console.log('📱 Showing password modal...');
            const authenticated = await passwordModal.show();
            
            if (authenticated) {
                    
                // Load credentials in parallel for faster authentication
                await Promise.all([
                    // Load Hyperliquid credentials (critical path)
                    window.PasswordManager.loadApiCredentials()
                        .then(() => {
                            // console.log('✅ Hyperliquid credentials loaded');
                        })
                        .catch(error => {
                            console.warn('⚠️ Hyperliquid credentials not found');
                        }),
                    
                    // Load Lighter credentials in parallel (non-blocking)
                    (async () => {
                        if (window.LighterCredentialsManager) {
                            try {
                                const lighterCreds = await window.LighterCredentialsManager.loadLighterCredentials();
                                // console.log('✅ Lighter credentials loaded');
                                
                                // Initialize Lighter client if credentials exist
                                if (lighterCreds && lighterCreds.apiKey && window.LighterClient) {
                                    await window.LighterClient.initialize(
                                        lighterCreds.apiKey,
                                        lighterCreds.publicAddress,
                                        lighterCreds.accountIndex,
                                        lighterCreds.apiKeyIndex,
                                        lighterCreds.network
                                    );
                                    // console.log('✅ Lighter client initialized');
                                }
                            } catch (error) {
                                console.warn('⚠️ Lighter credentials error:', error.message);
                            }
                        }
                    })()
                ]);
                
                return true;
            } else {
                return false;
            }
            
        } catch (error) {
            this.showError('Authentication failed: ' + error.message);
            return false;
        }
    }

    /**
     * Initialize all components
     */
    async initializeComponents() {
        // Initialize trade details panel
        if (!window.tradeDetailsPanel) {
            window.tradeDetailsPanel = new window.TradeDetailsPanel();
        }
        
        // Initialize TradeDataManager if available
        if (window.TradeDataManager) {
            // console.log('TradeDataManager available - enabling global data management');
        }
        
        // Start price service in background (non-blocking)
        if (window.BrowserPriceService) {
            window.BrowserPriceService.start()
                .then(() => {
                    // console.log('BrowserPriceService started successfully');
                })
                .catch(error => {
                    console.error('Failed to start price service:', error);
                });
        }
        
        // Components are already initialized as singletons
        // Just verify they're ready
        if (!window.TableComponent || !window.ModalComponent) {
            // console.log('Waiting for components to load...');
            await new Promise(resolve => setTimeout(resolve, 100));
            
            if (!window.TableComponent || !window.ModalComponent) {
                throw new Error('Required components not loaded');
            }
        }
    }

    /**
     * Initialize navigation system
     */
    async initializeNavigation() {
        // Instantiate navigation components on-demand for faster initial load
        if (!window.LeftSidebar && window.LeftSidebarClass) {
            window.LeftSidebar = new window.LeftSidebarClass();
        }
        
        if (!window.NavigationManager && window.NavigationManagerClass) {
            window.NavigationManager = new window.NavigationManagerClass();
        }
        
        // Wait for components to be ready if still loading
        if (!window.LeftSidebar || !window.TopMenuBar || !window.NavigationManager) {
            await new Promise(resolve => setTimeout(resolve, 100));
            
            if (!window.LeftSidebar || !window.TopMenuBar || !window.NavigationManager) {
                throw new Error('Navigation components not loaded');
            }
        }
        
        // Navigation components initialize themselves
        // NavigationManager handles the coordination
    }

    /**
     * Initialize order execution system
     */
    async initializeOrderSystem() {
        try {
            // Wait for order system components to be ready
            if (!window.orderClient || !window.NotificationManager) {
                // console.log('Waiting for order system components to load...');
                await new Promise(resolve => setTimeout(resolve, 100));
                
                if (!window.orderClient || !window.NotificationManager) {
                    // console.warn('Order system components not loaded - trading functionality will be disabled');
                    return;
                }
            }

            // Initialize NotificationManager in background (non-blocking, independent)
            if (window.NotificationManager && typeof window.NotificationManager.initialize === 'function') {
                window.NotificationManager.initialize().catch(err => 
                    console.warn('NotificationManager init failed:', err)
                );
            }
            
            const credentials = window.PasswordManager.getApiCredentials();
            // console.log('DEBUG: Checking credentials for orderClient:', {
            //     credentialsExist: !!credentials,
            //     hasApiKey: credentials && !!credentials.apiKey,
            //     hasPublicAddress: credentials && !!credentials.publicAddress,
            //     apiKeyLength: credentials?.apiKey?.length || 0,
            //     publicAddress: credentials?.publicAddress || 'none',
            //     isAuthenticated: window.PasswordManager.isAuthenticated
            // });
            
            if (credentials && credentials.apiKey) {
                const initSuccess = await window.orderClient.initialize(credentials.apiKey, credentials.publicAddress);
                if (initSuccess) {
                    // console.log('Order client system initialized successfully');
                    
                    // Initialize Auto Trade Bot with user address
                    if (window.BrowserAutoTradeBot && credentials.publicAddress) {
                        window.BrowserAutoTradeBot.setUserAddress(credentials.publicAddress);
                        // console.log('🤖 Auto Trade Bot: User address set');
                    }
                    
                    // Initialize Cancellation Bot with user address
                    if (window.BrowserCancellationBot && credentials.publicAddress) {
                        window.BrowserCancellationBot.setUserAddress(credentials.publicAddress);
                        // console.log('🤖 Cancellation Bot: User address set');
                    }
                    
                    // Check builder fee approval in background (non-blocking)
                    window.BrowserHyperliquidClient.checkBuilderFeeApproval().then(builderCheck => {
                        // Delay notification to ensure animation system is ready
                        setTimeout(() => {
                            if (builderCheck.success && window.NotificationManager) {
                                if (builderCheck.approved) {
                                    window.NotificationManager.showNotification({
                                        type: 'success',
                                        title: 'Builder Fee Approved',
                                        message: `✅ Builder fee approved at ${builderCheck.approvedPercentage} - Ready to trade!`,
                                        duration: 5000
                                    });
                                } else {
                                    console.warn('⚠️ [APP-INIT] CRITICAL: Builder fee not approved!');
                                    window.NotificationManager.showNotification({
                                        type: 'warning',
                                        title: 'Builder Fee Not Approved',
                                        message: `⚠️ Builder fee not set - Orders may fail! <a href="https://atsetup.evplus.ai" target="_blank" style="color: #00BF63; text-decoration: underline; font-weight: 600;">Setup builder fee here →</a>`,
                                        duration: 0
                                    });
                                }
                            }
                        }, 3000);
                    }).catch(builderError => {
                        console.error('❌ [APP-INIT] Builder fee check error (non-critical):', builderError);
                    });
                    
                    // Check referral code in background (non-blocking, silent)
                    window.BrowserHyperliquidClient.checkAndSetReferralCode('EVPLUS')
                        .catch(() => {}); // Silent fail
                    
                    // Start Global Position Monitor (2-second polling for fast response)
                    if (window.BrowserPositionMonitor) {
                        window.BrowserPositionMonitor.start();
                        // console.log('🔄 Global Position Monitor: Started (2s polling)');
                    }
                } else {
                    // console.log('Failed to initialize order client system');
                }
            } else {
                console.warn('No API credentials available - Order client system not initialized');
                console.warn('User must authenticate via password modal first');
            }
                
        } catch (error) {
            console.error('Failed to initialize order execution system:', error);
            // Don't throw - app should continue without trading functionality
        }
    }

    /**
     * Initialize Mode Manager for Lighter/Hyperliquid switching
     */
    async initializeModeManager() {
        try {
            // console.log('🔥 [APP] Initializing Mode Manager...');
            
            if (window.ModeManager) {
                await window.ModeManager.init();
                // console.log('✅ [APP] Mode Manager initialized');
            } else {
                console.warn('⚠️ [APP] ModeManager not available - dual mode disabled');
            }
        } catch (error) {
            console.error('❌ [APP] Failed to initialize Mode Manager:', error);
            // Don't throw - app should continue in Hyperliquid-only mode
        }
    }

    /**
     * Setup application event listeners
     */
    setupEventListeners() {

        // Table row clicks
        if (window.TableComponent && window.TableComponent.onRowClicked) {
            window.TableComponent.onRowClicked((trade) => {
                this.handleTradeClick(trade);
            });
        }

        // SSE service events
        if (window.SSEService) {
            window.SSEService.on('connected', (data) => {
                this.handleSSEConnected(data);
            });

            window.SSEService.on('disconnected', (data) => {
                this.handleSSEDisconnected(data);
            });

            // Trades data is handled by trades-view.js to avoid duplication
            // window.SSEService.on('trades-data', (trades) => {
            //     this.handleTradesData(trades);
            // });

            window.SSEService.on('assets-data', (assets) => {
                this.handleAssetsData(assets);
            });

            window.SSEService.on('trade-details', (details) => {
                this.handleTradeDetails(details);
            });
        }

        if (window.SSEService) {
            window.SSEService.on('error', (data) => {
                console.error('SSE error:', data);
                if (window.Helpers && window.Helpers.getErrorType && window.Helpers.showError) {
                    const errorType = window.Helpers.getErrorType(data.message, data.statusCode);
                    window.Helpers.showError(data.message || 'Connection error occurred', 5000, errorType);
                    this.handleSecurityError(errorType, data.message);
                }
                this.updateConnectionStatus('error');
            });
        }

        // Refresh button
        const refreshBtn = document.getElementById('refresh-btn');
        if (refreshBtn) {
            refreshBtn.addEventListener('click', () => {
                this.refreshData();
            });
        }

        // Window events
        window.addEventListener('beforeunload', () => {
            this.cleanup();
        });
    }

    /**
     * Connect to SSE service (Browser and Electron)
     */
    async connectToSSE() {
        try {
            // Wait for Environment to be available
            let retries = 0;
            while (!window.Environment && retries < 50) {
                await new Promise(resolve => setTimeout(resolve, 100));
                retries++;
            }
            
            if (!window.Environment) {
                // console.warn('Environment not available after waiting - SSE may fail');
            }
            
            // console.log('Connecting to SSE...');
            this.updateConnectionStatus('connecting');
            
            if (window.SSEService) {
                await window.SSEService.connect();
            } else {
                throw new Error('SSEService not available');
            }
            
        } catch (error) {
            console.error('SSE connection failed:', error);
            this.updateConnectionStatus('error');
        }
    }

    /**
     * Set application version from Electron
     */
    async setAppVersion() {
        try {
            if (window.electronAPI && window.electronAPI.getVersion) {
                const version = await window.electronAPI.getVersion();
                const versionElement = document.getElementById('app-version');
                if (versionElement) {
                    versionElement.textContent = `v${version}`;
                }
            }
        } catch (error) {
            // console.warn('Could not get app version:', error);
        }
    }

    /**
     * Set environment information for debugging
     */
    async setEnvironmentInfo() {
        try {
            if (this.environment) {
                const envInfo = this.environment.getDebugInfo();
                // console.log('Environment Info:', envInfo);
                
                // Add environment indicator to UI if in development
                if (this.environment.isDevelopment()) {
                    const indicator = document.createElement('div');
                    indicator.id = 'env-indicator';
                    indicator.textContent = 'DEV MODE';
                    indicator.style.cssText = 'position:fixed;top:5px;right:5px;background:#f38ba8;color:#1e1e2e;padding:2px 6px;font-size:10px;z-index:9999;border-radius:3px;';
                    document.body.appendChild(indicator);
                }
            }
        } catch (error) {
            // console.warn('Could not set environment info:', error);
        }
    }

    /**
     * Handle SSE connection established
     * @param {Object} data - Connection data
     */
    handleSSEConnected(data) {
        // console.log('SSE connected:', data);
        this.updateConnectionStatus('connected');
        // Removed old loading system call
        this.reconnectAttempts = 0;
    }

    /**
     * Handle SSE disconnection
     * @param {Object} data - Disconnection data
     */
    handleSSEDisconnected(data) {
        // console.log('SSE disconnected:', data);
        this.updateConnectionStatus('disconnected');
        if (window.Helpers && window.Helpers.showError) {
            window.Helpers.showError('Connection lost. Attempting to reconnect...');
        }
    }

    /**
     * Handle trades data from SSE
     * @param {Array} trades - Array of trade objects
     */
    handleTradesData(trades) {
        // console.log(`Received ${trades.length} trades from SSE`);
        
        this.allTrades = trades || [];
        // Let trades-view handle the filtering and table updates
        // this.applyCurrentFilters();
    }

    /**
     * Handle assets data from SSE
     * @param {Array} assets - Array of asset objects
     */
    handleAssetsData(assets) {
        // console.log(`Received ${assets.length} assets from SSE`);
        
        this.allAssets = assets || [];
    }

    /**
     * Handle trade details from SSE
     * @param {Object} details - Trade details object
     */
    handleTradeDetails(details) {
        // console.log('Received trade details:', details);
        ModalComponent.updateTradeDetails(details);
    }


    /**
     * Handle trade row click
     * @param {Object} trade - Trade object
     */
    handleTradeClick(trade) {
        // console.log('Trade clicked:', trade.id);
        if (window.ModalComponent) {
            window.ModalComponent.showTradeDetails(trade);
        }
    }


    /**
     * Handle security-related errors
     * @param {string} errorType - Type of error
     * @param {string} message - Error message
     */
    handleSecurityError(errorType, message) {
        const statusElement = document.getElementById('connection-status');
        if (!statusElement) return;
        
        // Update status text based on security error type
        switch (errorType) {
            case 'rate_limit':
                statusElement.textContent = 'Rate Limited - Please Wait';
                statusElement.className = 'connection-status rate-limited';
                break;
            case 'connection':
                statusElement.textContent = 'Connection Limit Reached';
                statusElement.className = 'connection-status connection-limited';
                break;
            case 'auth':
                statusElement.textContent = 'Authentication Failed';
                statusElement.className = 'connection-status auth-failed';
                break;
            default:
                this.updateConnectionStatus('error');
        }
    }

    /**
     * Update connection status indicator
     * @param {string} state - Connection state
     */
    updateConnectionStatus(state) {
        const dot = document.getElementById('connection-dot');
        const text = document.getElementById('connection-text');
        
        if (!dot || !text) return;

        // Remove all status classes
        dot.classList.remove('connected', 'connecting');
        
        switch (state) {
            case 'connected':
                dot.classList.add('connected');
                text.textContent = 'Connected';
                break;
            case 'connecting':
                dot.classList.add('connecting');
                text.textContent = 'Connecting...';
                break;
            case 'error':
                text.textContent = 'Connection Error';
                break;
            default:
                text.textContent = 'Disconnected';
        }
    }

    /**
     * Refresh data from server
     */
    async refreshData() {
        try {
            // console.log('Refreshing data...');
            // Removed old loading system calls
            
            // Reconnect SSE to get fresh data (Browser and Electron)
            if (window.SSEService) {
                await window.SSEService.connect();
            }
            
        } catch (error) {
            console.error('Data refresh failed:', error);
            if (window.Helpers && window.Helpers.showError) {
                window.Helpers.showError('Failed to refresh data');
            }
        }
    }

    /**
     * Get application state
     */
    getAppState() {
        return {
            isInitialized: this.isInitialized,
            tradesCount: this.allTrades.length,
            assetsCount: this.allAssets.length,
            filteredTradesCount: this.filteredTrades ? this.filteredTrades.length : 0,
            connectionState: window.SSEService ? window.SSEService.getConnectionInfo() : { status: 'sse-unavailable' },
            currentFilters: FiltersComponent ? FiltersComponent.getCurrentFilters() : {},
            currentStats: StatsComponent ? StatsComponent.getCurrentStats() : {}
        };
    }

    /**
     * Export application data
     */
    exportData() {
        try {
            const data = {
                trades: this.filteredTrades || this.allTrades,
                assets: this.allAssets,
                stats: StatsComponent ? StatsComponent.exportStats() : {},
                filters: FiltersComponent ? FiltersComponent.getCurrentFilters() : {},
                exportedAt: new Date().toISOString()
            };
            
            const jsonContent = JSON.stringify(data, null, 2);
            const filename = `evplus_export_${new Date().toISOString().split('T')[0]}.json`;
            
            // Create download
            const blob = new Blob([jsonContent], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            link.click();
            URL.revokeObjectURL(url);
            
            // console.log('Data exported successfully');
            
        } catch (error) {
            console.error('Export failed:', error);
            Helpers.showError('Failed to export data');
        }
    }

    /**
     * Cleanup resources
     */
    cleanup() {
        // console.log('Cleaning up application...');
        
        const isElectron = window.process?.versions?.electron;
        if (isElectron && window.electronAPI?.window?.toggleFullscreen) {
            // Do nothing - just checking availability
        }
        
        // Stop browser position monitor
        if (!isElectron && window.BrowserPositionMonitor) {
            window.BrowserPositionMonitor.stop();
            // console.log(' Global Position Monitor: Stopped');
        }
        
        // Clear sensitive data from memory on tab close
        if (window.PasswordManager) {
            // Clear in-memory keys (encrypted data stays in localStorage)
            window.PasswordManager.masterPassword = null;
            window.PasswordManager.derivedKey = null;
            window.PasswordManager.apiKey = null;
            window.PasswordManager.publicAddress = null;
            window.PasswordManager.isAuthenticated = false;
            // console.log('🔒 Cleared sensitive data from memory');
        }
        
        // Clear Hyperliquid client keys
        if (window.BrowserHyperliquidClient) {
            window.BrowserHyperliquidClient.privateKey = null;
            window.BrowserHyperliquidClient.wallet = null;
        }
        
        // Clear any intervals or timeouts
        // Component cleanup is handled by individual components
    }
    /**
     * Handle application errors
     * @param {Error} error - Error object
     */
    handleError(error) {
        console.error('Application error:', error);
        if (window.Helpers && window.Helpers.showError) {
            window.Helpers.showError(error.message || 'An unexpected error occurred');
        }
    }
}

// Global error handler
window.addEventListener('error', (event) => {
    console.error('Global error:', event.error);
    if (window.app) {
        window.app.handleError(event.error);
    }
});

window.addEventListener('unhandledrejection', (event) => {
    // Ignore MetaMask injection errors (extension not installed/disabled)
    if (event.reason && 
        (event.reason.message?.includes('MetaMask') || 
         event.reason.toString()?.includes('MetaMask'))) {
        event.preventDefault(); // Prevent default logging
        return;
    }
    
    console.error('Unhandled promise rejection:', event.reason);
    if (window.app) {
        window.app.handleError(new Error(event.reason));
    }
});

// App initialization is now handled in index.html to avoid duplicate instances
// This prevents race conditions with the global error handlers

// Export for ES6 modules
export { App };

// Export for debugging
window.App = App;
